import { get } from "lodash"
import { numberFormatter } from "helpers/formatters/numbers"
import { generateQueryKey } from "connected-components/dashboard/helpers/query-key"
import { isIntradaySimulationEligible } from "../helpers"
import { getIntradayBatchMoment } from "helpers/intraday"

function getExposureSubquery(dateField) {
  return `exposure(contentSetId: $contentSetId) {
        ${dateField}
        factors {
          id
          name
          category
          net
          currencyExposure
        }
      }`
}

function buildQuery(shouldUseIntradayQuery) {
  if (shouldUseIntradayQuery) {
    // IntradayExposure exposes `timestamp: DateTime!`, not `date` — alias it so downstream select stays uniform
    const exposureSubquery = getExposureSubquery("date: timestamp")
    return `#multi-date-exposure
query MultiDateExposureQuery($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $on: Date!, $contentSetId: String) {
  model(id: $modelId) {
    simulation:intradaySimulation(positionSet: $positionSet, base: $base, on: $on) {
      ${exposureSubquery}
    }
  }
}`
  }

  const exposureSubquery = getExposureSubquery("date")
  return `#multi-date-exposure
query MultiDateExposureQuery($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $from: Date!, $to: Date!, $contentSetId: String) {
  model(id: $modelId) {
    simulation(positionSet: $positionSet, base: $base, from: $from, to: $to, interval: AUTO) {
      ${exposureSubquery}
    }
  }
}`
}

const DAY_BASED_QUERY = buildQuery(false)
const INTRADAY_QUERY = buildQuery(true)

// intradaySimulationBatch returns plain `Exposure` (a `date`, not a `timestamp`) same as the day-based branch.
const BATCH_TREND_QUERY = `#multi-date-exposure-batch
query MultiDateExposureBatchQuery($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $on: Date!, $batchId: Id, $contentSetId: String) {
  model(id: $modelId) {
    simulation:intradaySimulationBatch(positionSet: $positionSet, base: $base, on: $on, batchId: $batchId) {
      ${getExposureSubquery("date")}
    }
  }
}`

function mapExposureRow(dateData) {
  // A live tick can arrive before the server has finished computing this row's factors.
  return (dateData.factors ?? []).reduce((acc, { id, net }) => {
    acc[id] = net
    return acc
  }, {})
}

// One query per committed batch; the template merges these with the live-tick query (graph/helpers.js extractDatesFromEachSeries).
export function createExposureIntradayBatchTrendQueries(modelId, positionSet, base, batches) {
  return batches.map(batch => {
    const key = generateQueryKey("exposure.multiDate.batch", { modelId, positionSet, base, on: batch.date, batchId: batch.id })

    return {
      key,
      skipInvalidation: true,
      query: BATCH_TREND_QUERY,
      variables: { modelId, positionSet, base, on: batch.date, batchId: batch.id },
      select: (graphqlResult) => {
        // intradaySimulationBatch is single-instant — one row in, one point out.
        const [row] = get(graphqlResult, "data.model.simulation.exposure", [])
        const batchMoment = getIntradayBatchMoment(batch)
        return {
          values: {
            formatter: numberFormatter,
            // Drop rather than chart a malformed date/asOf — an Invalid Date would corrupt sort order.
            dates: row && batchMoment.isValid() ? [{
              date: batchMoment.toISOString(),
              batchId: batch.id,
              ...mapExposureRow(row),
            }] : [],
          },
        }
      },
    }
  })
}

export function createExposureMultiDateQuery(modelId, positionSet, base, from, to, isIntradayMode, options = {}) {
  const shouldUseIntradaySimulation = isIntradaySimulationEligible(isIntradayMode, from, to, options.batchId, options.lastIntradayBatchId)

  const key = generateQueryKey("exposure.multiDate", {
    modelId, positionSet, base, from, to, intraday: shouldUseIntradaySimulation || undefined,
  })

  const sharedVariables = { modelId, positionSet, base }

  const variables = shouldUseIntradaySimulation
    ? { ...sharedVariables, on: from }
    : { ...sharedVariables, from, to }

  return {
    key,
    query: shouldUseIntradaySimulation ? INTRADAY_QUERY : DAY_BASED_QUERY,
    variables,
    select: (graphqlResult) => {
      const exposure = get(graphqlResult, "data.model.simulation.exposure", [])

      return {
        formatter: numberFormatter,
        values: {
          dates: exposure.map(({ date, ...dateData }) => ({
            date,
            ...mapExposureRow(dateData),
          })),
        },
      }
    },
  }
}
